import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Alert,
  CircularProgress,
  Autocomplete,
  Grid,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Chip,
  Paper,
  Stack,
  Link,
} from '@mui/material';
import {
  EventAvailable as ScheduleIcon,
  Send as SendIcon,
  PersonAdd as PersonAddIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenIcon,
  AutoAwesome as PrefillIcon,
} from '@mui/icons-material';
import logger from '../../utils/logger';
import SendInvoiceButton from './SendInvoiceButton';
import NotifyCustomerButton from './NotifyCustomerButton';
import AddCustomerDialog from './AddCustomerDialog';
import JobLineItemsEditor, { extrasTotal, extrasValid, fromApiLineItems, toApiLineItems } from './JobLineItemsEditor';
import { JOB_TYPES, SERVICE_TYPES, formatDuration, durationChoices } from '../../constants/jobOptions';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isoDate = (d) => {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const tomorrowIso = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return isoDate(d);
};


const formatShortDate = (iso) =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const EMPTY_FORM = {
  jobType: 'RESIDENTIAL',
  serviceType: 'regular',
  scheduledDate: tomorrowIso(),
  scheduledTime: '09:00',
  estimatedDuration: 120,
  totalAmount: '',
  specialInstructions: '',
  useProfileAddress: true,
  address: { street: '', street_line_2: '', city: '', state: '', zip: '' },
};

/**
 * Admin dialog: create and schedule a job for a client, and email the invoice
 * in the same step. Picking a client pre-fills the form from their last job.
 */
const ScheduleJobDialog = ({ open, onClose, onCreated, initialClientProfileId = null }) => {
  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }),
    []
  );

  const [clients, setClients] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);

  const [client, setClient] = useState(null);
  const [email, setEmail] = useState('');
  // Off = send this invoice to another address (e.g. the admin, for a test) without changing the profile
  const [saveEmail, setSaveEmail] = useState(true);
  const [selectedTechnicians, setSelectedTechnicians] = useState([]);
  // Payout per technician in dollars, as typed: { [userId]: '70' }
  const [payouts, setPayouts] = useState({});
  // Add-on services (the main service price is form.totalAmount)
  const [extras, setExtras] = useState([]);
  const [mainTechnicianId, setMainTechnicianId] = useState(null); // Who does the main service (null = whole team)
  const [form, setForm] = useState(EMPTY_FORM);
  const [prefill, setPrefill] = useState(null);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);

  const [submitting, setSubmitting] = useState('');
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);
  const [invoiceResult, setInvoiceResult] = useState(null); // { recipient, url } | { error }
  const [copied, setCopied] = useState(false);

  const resetForm = () => {
    setClient(null);
    setEmail('');
    setSaveEmail(true);
    setSelectedTechnicians([]);
    setPayouts({});
    setExtras([]);
    setMainTechnicianId(null);
    setForm({ ...EMPTY_FORM, scheduledDate: tomorrowIso() });
    setPrefill(null);
    setError('');
    setCreated(null);
    setInvoiceResult(null);
    setCopied(false);
  };

  // Pre-fill from the client's last job (service, price, time, technicians…)
  const selectClient = useCallback(async (value, technicianList) => {
    setClient(value);
    setEmail(value?.email || '');
    setSaveEmail(true);
    setPrefill(null);
    if (!value) return;

    setPrefillLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/jobs/last-for-client/${value.clientProfileId}`, { headers: authHeaders });
      if (!res.ok) return;
      const { lastJob, suggestedDate, cadenceDays } = await res.json();
      if (!lastJob) return;

      // Last job's add-ons come back too; its total then splits into main + add-ons
      const lastLines = fromApiLineItems(lastJob.lineItems, { inCents: false });
      const lastMainPrice = lastLines.mainPrice ?? lastJob.totalAmount;
      setExtras(lastLines.extras);
      setMainTechnicianId(lastLines.mainTechnicianId);
      setForm((prev) => ({
        ...prev,
        jobType: lastJob.jobType || prev.jobType,
        serviceType: lastJob.serviceType || prev.serviceType,
        scheduledTime: lastJob.scheduledTime || prev.scheduledTime,
        estimatedDuration: lastJob.estimatedDuration || prev.estimatedDuration,
        totalAmount: lastMainPrice != null ? String(lastMainPrice) : prev.totalAmount,
        specialInstructions: lastJob.specialInstructions || '',
        scheduledDate: suggestedDate || prev.scheduledDate,
      }));
      const available = technicianList || [];
      const kept = lastJob.technicians.filter((t) => available.some((a) => a.userId === t.userId));
      setSelectedTechnicians(kept.map((t) => available.find((a) => a.userId === t.userId)));
      setPayouts(Object.fromEntries(
        kept.filter((t) => t.payout != null).map((t) => [t.userId, String(t.payout)])
      ));
      setPrefill({ jobNumber: lastJob.jobNumber, date: lastJob.scheduledDate, cadenceDays, suggestedDate });
    } catch (err) {
      logger.log('[ScheduleJobDialog] Prefill failed:', err.message);
    } finally {
      setPrefillLoading(false);
    }
  }, [apiBase, authHeaders]);

  // Load clients + technicians (optionally selecting a client afterwards)
  const loadLists = useCallback(async ({ selectUserId = null, selectProfileId = null } = {}) => {
    setLoadingLists(true);
    setError('');
    try {
      const [clientsRes, techRes] = await Promise.all([
        fetch(`${apiBase}/api/users/clients`, { headers: authHeaders }),
        fetch(`${apiBase}/api/maids/light?activeOnly=true&limit=500`, { headers: authHeaders }),
      ]);
      if (!clientsRes.ok) throw new Error('Failed to load clients');
      if (!techRes.ok) throw new Error('Failed to load technicians');

      const clientsJson = await clientsRes.json();
      const techJson = await techRes.json();

      const rawClients = Array.isArray(clientsJson) ? clientsJson : clientsJson.clients || [];
      const mappedClients = rawClients
        .filter((c) => c.clientProfile?.id)
        .map((c) => ({
          clientProfileId: c.clientProfile.id,
          userId: c.id,
          name: `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email,
          email: c.email,
          phone: c.phoneNumber,
          address: c.address,
          city: c.city,
          state: c.state,
          zipCode: c.zipCode,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      const rawTechs = Array.isArray(techJson) ? techJson : techJson.data || [];
      const mappedTechs = rawTechs
        .filter((t) => t.userId || t.id)
        .map((t) => ({
          userId: t.userId || t.id,
          name: `${t.firstName || ''} ${t.lastName || ''}`.trim(),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setClients(mappedClients);
      setTechnicians(mappedTechs);

      const preset = mappedClients.find(
        (c) => (selectProfileId && c.clientProfileId === selectProfileId) || (selectUserId && c.userId === selectUserId)
      );
      if (preset) selectClient(preset, mappedTechs);
    } catch (err) {
      logger.error('[ScheduleJobDialog] Failed to load lists:', err);
      setError(err.message || 'Failed to load clients and technicians');
    } finally {
      setLoadingLists(false);
    }
  }, [apiBase, authHeaders, selectClient]);

  useEffect(() => {
    if (open) loadLists({ selectProfileId: initialClientProfileId });
  }, [open, initialClientProfileId, loadLists]);

  const resetAndClose = () => {
    resetForm();
    onClose();
  };

  const setField = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const setAddressField = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, address: { ...prev.address, [field]: value } }));
  };

  const profileAddressText = client
    ? [client.address, client.city, client.state, client.zipCode].filter(Boolean).join(', ')
    : '';

  // Customer price: the main service plus any add-ons
  const price = Number(form.totalAmount) + extrasTotal(extras);
  const emailValid = EMAIL_RE.test(email.trim());
  const emailChanged = !!client && email.trim().toLowerCase() !== (client.email || '').toLowerCase();

  const canScheduleBase =
    !!client &&
    !!form.scheduledDate &&
    !!form.scheduledTime &&
    Number(form.estimatedDuration) > 0 &&
    (form.useProfileAddress ? !!profileAddressText : !!form.address.street) &&
    (!emailChanged || !saveEmail || emailValid) &&
    (extras.length === 0 || (form.totalAmount !== '' && extrasValid(extras)));
  const payoutOf = (userId) => Number(payouts[userId]);
  const payoutsComplete = selectedTechnicians.every((t) => payoutOf(t.userId) > 0);
  const totalPayout = selectedTechnicians.reduce((sum, t) => sum + (payoutOf(t.userId) || 0), 0);
  const canSchedule = canScheduleBase && payoutsComplete;
  const canSend = canSchedule && price > 0 && emailValid;

  const durationOptions = durationChoices(form.estimatedDuration);

  const handleSubmit = async (sendInvoice) => {
    if (sendInvoice ? !canSend : !canSchedule) return;
    setSubmitting(sendInvoice ? 'send' : 'schedule');
    setError('');
    try {
      // 1. Save a corrected email first, so the job and invoice use it
      if (emailChanged && saveEmail) {
        const res = await fetch(`${apiBase}/api/users/clients/${client.clientProfileId}/email`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ email: email.trim() }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Failed to update the customer email');
        setClient((prev) => ({ ...prev, email: data.email }));
        setClients((prev) => prev.map((c) => (c.clientProfileId === client.clientProfileId ? { ...c, email: data.email } : c)));
      }

      // 2. Create the job
      const payload = {
        clientProfileId: client.clientProfileId,
        jobType: form.jobType,
        serviceType: form.serviceType,
        scheduledDate: form.scheduledDate,
        scheduledTime: form.scheduledTime,
        estimatedDuration: Number(form.estimatedDuration),
        totalAmount: form.totalAmount === '' ? null : Number(form.totalAmount),
        specialInstructions: form.specialInstructions || null,
        address: form.useProfileAddress ? null : form.address,
        technicianIds: selectedTechnicians.map((t) => t.userId),
        technicianPayouts: Object.fromEntries(
          selectedTechnicians.map((t) => [t.userId, payoutOf(t.userId)])
        ),
        lineItems: toApiLineItems(form.totalAmount, extras, { mainTechnicianId }),
      };
      const response = await fetch(`${apiBase}/api/jobs/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create job');
      }
      setCreated(data.job);
      if (onCreated) onCreated(data.job);

      // 3. Email the invoice (the job exists even if this fails — retry from the success screen)
      if (sendInvoice) {
        try {
          const invRes = await fetch(`${apiBase}/api/jobs/${data.job.id}/send-invoice`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders },
            body: JSON.stringify({ to: email.trim() }),
          });
          const invData = await invRes.json().catch(() => ({}));
          if (!invRes.ok) throw new Error(invData.error || 'Failed to send the invoice');
          setInvoiceResult({ recipient: invData.recipient, url: invData.url });
        } catch (invErr) {
          logger.error('[ScheduleJobDialog] Invoice send failed:', invErr);
          setInvoiceResult({ error: invErr.message });
        }
      }
    } catch (err) {
      logger.error('[ScheduleJobDialog] Failed to create job:', err);
      setError(err.message || 'Failed to create job');
    } finally {
      setSubmitting('');
    }
  };

  const copyPayLink = async () => {
    try {
      await navigator.clipboard.writeText(invoiceResult.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.log('[ScheduleJobDialog] Clipboard failed:', err.message);
    }
  };

  const renderSuccess = () => (
    <Box sx={{ py: 2 }}>
      <Alert severity="success" sx={{ mb: 2 }}>
        Job <strong>{created.jobNumber}</strong> was scheduled for {client?.name} on{' '}
        {formatShortDate(form.scheduledDate)} at {form.scheduledTime}.
      </Alert>

      {invoiceResult?.recipient && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Invoice emailed to <strong>{invoiceResult.recipient}</strong> ({money(price)}).
          </Typography>
          {invoiceResult.url && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Button size="small" variant="outlined" startIcon={<CopyIcon />} onClick={copyPayLink}>
                {copied ? 'Copied!' : 'Copy pay link'}
              </Button>
              <Link href={invoiceResult.url} target="_blank" rel="noopener" underline="hover" variant="body2">
                Open invoice <OpenIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} />
              </Link>
            </Stack>
          )}
        </Paper>
      )}

      {invoiceResult?.error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The job was created, but the invoice could not be sent: {invoiceResult.error}. Use Send Invoice below to retry.
        </Alert>
      )}

      {!invoiceResult && (
        <Typography variant="body2" color="text.secondary">
          It will appear on the Service Schedule and in the technician app for anyone assigned. You can email the
          invoice now, or later from the job details.
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog open={open} onClose={submitting ? undefined : resetAndClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ScheduleIcon color="primary" />
        Schedule a Job
      </DialogTitle>

      <DialogContent dividers>
        {created ? (
          renderSuccess()
        ) : (
          <Box sx={{ pt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {loadingLists ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={2}>
                {/* Client */}
                <Grid item xs={12}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Autocomplete
                      sx={{ flexGrow: 1 }}
                      options={clients}
                      value={client}
                      onChange={(_, value) => selectClient(value, technicians)}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(a, b) => a.clientProfileId === b.clientProfileId}
                      renderOption={(props, option) => (
                        <li {...props} key={option.clientProfileId}>
                          <Box>
                            <Typography variant="body2">{option.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {[option.email, option.phone].filter(Boolean).join(' · ')}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="Client" required placeholder="Search by name" />
                      )}
                      noOptionsText={clients.length ? 'No matching client — use “New customer”' : 'No clients found'}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<PersonAddIcon />}
                      onClick={() => setAddCustomerOpen(true)}
                      sx={{ mt: 1, whiteSpace: 'nowrap' }}
                    >
                      New customer
                    </Button>
                  </Stack>
                </Grid>

                {/* Customer at a glance: email is editable (saved before the job is created) */}
                {client && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            type="email"
                            label="Customer email (invoice goes here)"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            error={!!email && !emailValid}
                            helperText={
                              !email
                                ? 'No email on file — add one to send the invoice.'
                                : !emailChanged
                                  ? ' '
                                  : saveEmail
                                    ? 'Will be saved to the customer profile (also their login email).'
                                    : `This invoice only — the profile keeps ${client.email || 'no email'}.`
                            }
                          />
                          {emailChanged && (
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={saveEmail}
                                  onChange={(e) => setSaveEmail(e.target.checked)}
                                />
                              }
                              label={<Typography variant="body2">Save to customer profile</Typography>}
                            />
                          )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Phone: {client.phone || '—'}
                          </Typography>
                          <Typography variant="body2" color={profileAddressText ? 'text.secondary' : 'error'}>
                            Address: {profileAddressText || 'none on file'}
                          </Typography>
                        </Grid>
                      </Grid>
                      {prefillLoading && (
                        <Typography variant="caption" color="text.secondary">Loading last job…</Typography>
                      )}
                      {prefill && (
                        <Chip
                          icon={<PrefillIcon />}
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ mt: 1 }}
                          label={
                            `Pre-filled from ${prefill.jobNumber} (${formatShortDate(prefill.date)})` +
                            (prefill.suggestedDate
                              ? ` · every ${prefill.cadenceDays / 7 === 1 ? 'week' : `${prefill.cadenceDays / 7} weeks`}, next ${formatShortDate(prefill.suggestedDate)}`
                              : '')
                          }
                        />
                      )}
                    </Paper>
                  </Grid>
                )}

                {/* Job & service type */}
                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth label="Job Type" value={form.jobType} onChange={setField('jobType')}>
                    {JOB_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth label="Service" value={form.serviceType} onChange={setField('serviceType')}>
                    {SERVICE_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Schedule */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    required
                    type="date"
                    label="Date"
                    value={form.scheduledDate}
                    onChange={setField('scheduledDate')}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    required
                    type="time"
                    label="Start Time (Eastern)"
                    value={form.scheduledTime}
                    onChange={setField('scheduledTime')}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 900 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Duration"
                    value={Number(form.estimatedDuration)}
                    onChange={setField('estimatedDuration')}
                  >
                    {durationOptions.map((minutes) => (
                      <MenuItem key={minutes} value={minutes}>{formatDuration(minutes)}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Price */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label={extras.length ? 'Main service price ($)' : 'Price ($)'}
                    value={form.totalAmount}
                    onChange={setField('totalAmount')}
                    inputProps={{ min: 0, step: 5 }}
                    error={extras.length > 0 && form.totalAmount === ''}
                    helperText={price > 0 ? `Customer will be charged ${money(price)}` : 'Required to send the invoice'}
                  />
                </Grid>

                {/* Technicians */}
                <Grid item xs={12} sm={8}>
                  <Autocomplete
                    multiple
                    options={technicians}
                    value={selectedTechnicians}
                    onChange={(_, value) => setSelectedTechnicians(value)}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(a, b) => a.userId === b.userId}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.userId}
                          label={option.name}
                          size="small"
                          color={index === 0 ? 'primary' : 'default'}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Assign Technicians (optional)"
                        helperText="First selected becomes lead. Leave empty to assign later."
                      />
                    )}
                  />
                </Grid>

                {/* Payout per technician (required) */}
                {selectedTechnicians.length > 0 && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Technician payout</Typography>
                      <Grid container spacing={1.5}>
                        {selectedTechnicians.map((t, index) => (
                          <Grid item xs={12} sm={6} key={t.userId}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              required
                              label={`${t.name}${index === 0 ? ' (lead)' : ''}`}
                              value={payouts[t.userId] ?? ''}
                              onChange={(e) => setPayouts((prev) => ({ ...prev, [t.userId]: e.target.value }))}
                              error={payouts[t.userId] !== undefined && payouts[t.userId] !== '' && !(payoutOf(t.userId) > 0)}
                              InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
                              inputProps={{ min: 0, step: 5 }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                      <Typography variant="caption" color={payoutsComplete ? 'text.secondary' : 'error'} display="block" sx={{ mt: 1 }}>
                        {payoutsComplete
                          ? `Total payout ${money(totalPayout)}${price > 0 ? ` (${Math.round((totalPayout / price) * 100)}% of the price)` : ''}`
                          : 'Enter a payout for each technician'}
                      </Typography>
                    </Paper>
                  </Grid>
                )}

                {/* Add-on services: own price each, optionally done by one technician */}
                <Grid item xs={12}>
                  <JobLineItemsEditor
                    extras={extras}
                    onChange={setExtras}
                    technicians={selectedTechnicians}
                    mainPrice={form.totalAmount}
                    main={{
                      label: SERVICE_TYPES.find((t) => t.value === form.serviceType)?.label || 'Cleaning',
                      price: form.totalAmount,
                      technicianId: mainTechnicianId,
                      onTechnicianChange: setMainTechnicianId,
                    }}
                  />
                </Grid>

                {/* Address */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.useProfileAddress}
                        onChange={setField('useProfileAddress')}
                        disabled={!client}
                      />
                    }
                    label="Use the client's address on file"
                  />
                  {form.useProfileAddress && client && !profileAddressText && (
                    <Typography variant="body2" color="error" sx={{ ml: 1 }}>
                      This client has no address on file. Enter one below.
                    </Typography>
                  )}
                </Grid>

                {!form.useProfileAddress && (
                  <>
                    <Grid item xs={12} sm={8}>
                      <TextField fullWidth required label="Street" value={form.address.street} onChange={setAddressField('street')} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Apt / Unit" value={form.address.street_line_2} onChange={setAddressField('street_line_2')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="City" value={form.address.city} onChange={setAddressField('city')} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField fullWidth label="State" value={form.address.state} onChange={setAddressField('state')} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField fullWidth label="ZIP" value={form.address.zip} onChange={setAddressField('zip')} />
                    </Grid>
                  </>
                )}

                {/* Instructions */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Special Instructions"
                    value={form.specialInstructions}
                    onChange={setField('specialInstructions')}
                    placeholder="Gate code, pets, areas to focus on..."
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {created ? (
          <>
            {(!invoiceResult || invoiceResult.error) && (
              <SendInvoiceButton jobId={created.id} jobNumber={created.jobNumber} size="medium" />
            )}
            <NotifyCustomerButton jobId={created.id} jobNumber={created.jobNumber} size="medium" />
            <Box sx={{ flexGrow: 1 }} />
            <Button onClick={resetForm}>Schedule Another</Button>
            <Button variant="contained" onClick={resetAndClose}>Done</Button>
          </>
        ) : (
          <>
            <Button onClick={resetAndClose} disabled={!!submitting}>Cancel</Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              onClick={() => handleSubmit(false)}
              disabled={!canSchedule || !!submitting || loadingLists}
              startIcon={submitting === 'schedule' ? <CircularProgress size={18} color="inherit" /> : <ScheduleIcon />}
            >
              {submitting === 'schedule' ? 'Scheduling...' : 'Schedule only'}
            </Button>
            <Button
              variant="contained"
              onClick={() => handleSubmit(true)}
              disabled={!canSend || !!submitting || loadingLists}
              startIcon={submitting === 'send' ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
            >
              {submitting === 'send' ? 'Scheduling & sending...' : 'Schedule & Send Invoice'}
            </Button>
          </>
        )}
      </DialogActions>

      <AddCustomerDialog
        open={addCustomerOpen}
        onClose={() => setAddCustomerOpen(false)}
        onCustomerAdded={(user) => {
          // Reload the client list and select the new customer
          if (user?.id) loadLists({ selectUserId: user.id });
        }}
      />
    </Dialog>
  );
};

export default ScheduleJobDialog;
